import { useEffect, useState } from 'react';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import { Typography } from '../../../components/typography/typography';
import { Order, OrderPaymentMethod } from '../../../clients/quipli-service/order/order';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { OrderPaymentMethodSelector } from '../../payment/payment-method-selector';
import { Location } from '../../../clients/quipli-service/location/location';
import { CustomerDocuments } from '../create/customer-info';
import { toast } from '../../../util/toasts';
import { Spinner } from '../../../components/spinner/spinner';
import {
  CashPaymentMethod,
  CheckPaymentMethod,
  CustomPaymentMethod,
  OnAccountPaymentMethodRequest,
  PaymentMethodRequest,
  PaymentMethodType,
  StripePaymentMethodRequest,
} from '../../../clients/quipli-service/payment/payment';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { PaymentContextProvider } from '../../../context/payment-context';
import { OnAccount } from '../../../clients/quipli-service/payment/on-account/on-account';

export const ApproveQuoteModal = ({ orderId }: { orderId: number }) => {
  const { getOrderById, getLocationById, canSubmit, approveQuote } = useAPI();
  const { captureCard } = usePaymentApiV2();

  const [order, setOrder] = useState<Order>();
  const [location, setLocation] = useState<Location>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [paymentType, setPaymentType] = useState<OrderPaymentMethod>();
  const [confirmationTokenId, setConfirmationTokenId] = useState<string>();
  const [cardId, setCardId] = useState<number>();
  const [poNumber, setPoNumber] = useState<string>('');
  const [bankAccountId, setBankAccountId] = useState<string>();
  const [onAccount, setOnAccount] = useState<OnAccount>();
  const [customPaymentMethod, setCustomPaymentMethod] = useState<string>();

  const approve = async () => {
    let paymentMethod: PaymentMethodRequest;

    let _cardId = cardId;
    if (confirmationTokenId) {
      try {
        setIsLoading(true);
        _cardId = (await captureCard({ confirmationTokenId, userId: order!.user.id })).id;
        toast('Card captured successfully.', 'success');
      } catch (error: any) {
        toast(error.message || 'Something went wrong.', 'error');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    switch (paymentType) {
      case OrderPaymentMethod.CreditCard:
        paymentMethod = { type: PaymentMethodType.Stripe, cardId: _cardId, poNumber } as StripePaymentMethodRequest;
        break;
      case OrderPaymentMethod.BankAccount:
        paymentMethod = { type: PaymentMethodType.Stripe, bankAccountId, poNumber } as StripePaymentMethodRequest;
        break;
      case OrderPaymentMethod.OnAccount:
        paymentMethod = {
          type: PaymentMethodType.OnAccount,
          accountNumber: '',
          poNumber,
        } as OnAccountPaymentMethodRequest;
        break;
      case OrderPaymentMethod.Cash:
        paymentMethod = { type: PaymentMethodType.Cash, poNumber } as CashPaymentMethod;
        break;
      case OrderPaymentMethod.Check:
        paymentMethod = { type: PaymentMethodType.Check, poNumber } as CheckPaymentMethod;
        break;
      case OrderPaymentMethod.Custom:
        paymentMethod = {
          type: PaymentMethodType.Custom,
          name: customPaymentMethod,
          poNumber,
        } as CustomPaymentMethod;
        break;
      case OrderPaymentMethod.ProceedWithoutPayment:
      default:
        paymentMethod = { type: PaymentMethodType.None, poNumber };
        break;
    }

    try {
      setIsLoading(true);
      const _canApprove = await canSubmit(
        order!.user.id,
        paymentType ?? OrderPaymentMethod.ProceedWithoutPayment,
        cardId,
        false,
      );

      if (!_canApprove.isValid) {
        _canApprove.errors.forEach((error) => toast(error, 'error'));
        return;
      }

      await approveQuote(orderId, paymentMethod);

      setShowModal(false);
      window.location.reload();
    } catch (error: any) {
      toast(error.message || 'Something went wrong.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // fetch
    const load = async () => {
      const _order = await getOrderById(orderId);
      const _location = await getLocationById(_order.locationId);

      setOrder(_order);
      setLocation(_location);
      setPaymentType(
        _order?.paymentMethodType === OrderPaymentMethod.ProceedWithoutPayment && _location.allowNoPayment
          ? OrderPaymentMethod.ProceedWithoutPayment
          : OrderPaymentMethod.CreditCard,
      );
    };

    load();
  }, []);
  return (
    <>
      <div className="flex justify-end">
        <Button className="button button-secondary" onClick={() => setShowModal(true)}>
          <Typography type="text-sm" weight="semi-bold" className="text-grey-700">
            Approve Quote
          </Typography>
        </Button>
      </div>

      {order && location && (
        <Modal
          title="Approve Quote"
          isOpen={showModal}
          actions={
            isLoading ? (
              <Spinner />
            ) : (
              <>
                <Button className="button button-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  className="button button-yellow"
                  onClick={approve}
                  disabled={paymentType == OrderPaymentMethod.OnAccount && !onAccount}
                >
                  Approve
                </Button>
              </>
            )
          }
          close={() => setShowModal(false)}
        >
          <div>
            <div className="border-0 border-b border-solid border-grey-200 pb-[12px]">
              <div className="py-[16px]">
                <Typography type="text-md" weight="medium" className="text-grey-800">
                  Payment
                  <span className="ml-1 text-xs font-semibold text-error-800">*</span>
                </Typography>
              </div>

              <PaymentContextProvider>
                <OrderPaymentMethodSelector
                  customer={order.user}
                  paymentMethod={paymentType}
                  location={location}
                  setPaymentMethod={setPaymentType}
                  setCardId={setCardId}
                  setPoNumber={setPoNumber}
                  poNumber={poNumber}
                  setBankAccountId={setBankAccountId}
                  setOnAccount={setOnAccount}
                  setCard={(card) => {
                    if (card.confirmationTokenId) {
                      setConfirmationTokenId(card.confirmationTokenId);
                    } else {
                      setConfirmationTokenId(undefined);
                    }
                  }}
                  setCustomPaymentMethod={setCustomPaymentMethod}
                  customPaymentMethod={customPaymentMethod}
                />
              </PaymentContextProvider>
            </div>

            <CustomerDocuments customer={order.user} className="!px-0 !shadow-none" />
          </div>
        </Modal>
      )}
    </>
  );
};
